import { c as _c } from "react/compiler-runtime";
import { useDataFetchOrchestrator } from "@/hooks/use-data-fetch-orchestrator";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { useSearchPageMapData } from "@/providers/search";
import { Bounds } from "@/providers/search/stores/map-store";
import { debounce } from "@/utils/debounce";
import { logger } from "@/utils/logger";
import { transformFilters } from "@/utils/search/transform-filters";
import { useMap } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useRef } from "react";

/**
 * Hook to handle automatic property fetching when map bounds change or filters change
 * Segregates filter changes (data fetching) from bounds changes (bounds handling)
 * Optimized for performance with minimal re-renders and efficient debouncing
 */
export function useMapBoundsUpdater() {
  const $ = _c(20);
  const map = useMap();
  const setBounds = useSearchPageMapData(_temp);
  const {
    filters,
    setFilters
  } = useUrlFilters();
  const mode = useSearchPageMapData(_temp2);
  const fetchMapData = useSearchPageMapData(_temp3);
  const fetchNearByProjects = useSearchPageMapData(_temp4);
  let t0;
  if ($[0] !== fetchMapData || $[1] !== fetchNearByProjects || $[2] !== mode) {
    t0 = {
      mode,
      fetchMapData,
      fetchNearByProjects
    };
    $[0] = fetchMapData;
    $[1] = fetchNearByProjects;
    $[2] = mode;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    orchestrateFetch
  } = useDataFetchOrchestrator(t0);
  const isFirstTime = useRef(true);
  const listenerRef = useRef(null);
  const isMountedRef = useRef(false);
  const previousModeRef = useRef(mode);
  let t1;
  if ($[4] !== filters || $[5] !== map || $[6] !== orchestrateFetch || $[7] !== setBounds || $[8] !== setFilters) {
    t1 = () => {
      if (!map) {
        return;
      }
      requestAnimationFrame(() => {
        const bounds = map.getBounds()?.toJSON() as Bounds;
        if (!bounds) {
          return;
        }
        const validBounds = {
          north: Math.max(bounds.north, bounds.south),
          south: Math.min(bounds.north, bounds.south),
          east: Math.max(bounds.east, bounds.west),
          west: Math.min(bounds.east, bounds.west)
        };
        if (validBounds.north !== bounds.north || validBounds.south !== bounds.south || validBounds.east !== bounds.east || validBounds.west !== bounds.west) {
          logger.warn("[useMapBoundsUpdater] Fixed invalid bounds on first change", {
            original: bounds,
            fixed: validBounds
          });
        }
        setBounds(validBounds);
        if (isFirstTime.current) {
          isFirstTime.current = false;
          const {
            filtersForGraphQL,
            newFilters
          } = transformFilters({
            filters,
            selectedLocationId: filters.selectedLocationId,
            mapBounds: validBounds,
            mapZoom: map.getZoom(),
            from: "postSSR"
          });
          setFilters(newFilters);
          orchestrateFetch({
            filters: newFilters,
            map,
            filtersForGraphQL,
            isInitialFetch: true
          });
        }
      });
    };
    $[4] = filters;
    $[5] = map;
    $[6] = orchestrateFetch;
    $[7] = setBounds;
    $[8] = setFilters;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const handleBoundsChanged = t1;
  let t2;
  let t3;
  if ($[10] !== filters || $[11] !== map || $[12] !== mode || $[13] !== orchestrateFetch) {
    t2 = () => {
      if (!isMountedRef.current) {
        isMountedRef.current = true;
        previousModeRef.current = mode;
        if (mode !== "default" && map) {
          const bounds_0 = map.getBounds()?.toJSON() as Bounds;
          if (bounds_0) {
            const validBounds_0 = {
              north: Math.max(bounds_0.north, bounds_0.south),
              south: Math.min(bounds_0.north, bounds_0.south),
              east: Math.max(bounds_0.east, bounds_0.west),
              west: Math.min(bounds_0.east, bounds_0.west)
            };
            if (validBounds_0.north !== bounds_0.north || validBounds_0.south !== bounds_0.south || validBounds_0.east !== bounds_0.east || validBounds_0.west !== bounds_0.west) {
              logger.warn("[useMapBoundsUpdater] Fixed invalid bounds on mount", {
                original: bounds_0,
                fixed: validBounds_0
              });
            }
            const {
              filtersForGraphQL: filtersForGraphQL_0
            } = transformFilters({
              filters,
              selectedLocationId: filters.selectedLocationId,
              mapBounds: validBounds_0,
              mapZoom: map.getZoom(),
              from: "postSSR"
            });
            orchestrateFetch({
              filters,
              map,
              filtersForGraphQL: filtersForGraphQL_0,
              isInitialFetch: false
            });
          } else {
            logger.warn("[useMapBoundsUpdater] No bounds available on mount with non-default mode");
          }
        }
        return;
      }
      if (mode !== previousModeRef.current) {
        previousModeRef.current = mode;
        if (map) {
          const bounds_1 = map.getBounds()?.toJSON() as Bounds;
          if (bounds_1) {
            const validBounds_1 = {
              north: Math.max(bounds_1.north, bounds_1.south),
              south: Math.min(bounds_1.north, bounds_1.south),
              east: Math.max(bounds_1.east, bounds_1.west),
              west: Math.min(bounds_1.east, bounds_1.west)
            };
            if (validBounds_1.north !== bounds_1.north || validBounds_1.south !== bounds_1.south || validBounds_1.east !== bounds_1.east || validBounds_1.west !== bounds_1.west) {
              logger.warn("[useMapBoundsUpdater] Fixed invalid bounds for mode change", {
                original: bounds_1,
                fixed: validBounds_1
              });
            }
            const {
              filtersForGraphQL: filtersForGraphQL_1
            } = transformFilters({
              filters,
              selectedLocationId: filters.selectedLocationId,
              mapBounds: validBounds_1,
              mapZoom: map.getZoom(),
              from: "postSSR"
            });
            orchestrateFetch({
              filters,
              map,
              filtersForGraphQL: filtersForGraphQL_1,
              isInitialFetch: false
            });
          } else {
            logger.warn("[useMapBoundsUpdater] No bounds available for mode change");
          }
        } else {
          logger.warn("[useMapBoundsUpdater] No map available for mode change");
        }
      }
    };
    t3 = [mode, map, filters, orchestrateFetch];
    $[10] = filters;
    $[11] = map;
    $[12] = mode;
    $[13] = orchestrateFetch;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[16] !== handleBoundsChanged || $[17] !== map) {
    t4 = () => {
      if (!map || listenerRef.current) {
        return;
      }
      const debouncedHandleBoundsChanged = debounce(handleBoundsChanged, 200);
      const listener = map.addListener("bounds_changed", debouncedHandleBoundsChanged);
      listenerRef.current = listener;
      return () => {
        if (listenerRef.current) {
          listenerRef.current.remove();
          listenerRef.current = null;
        }
      };
    };
    t5 = [map, handleBoundsChanged];
    $[16] = handleBoundsChanged;
    $[17] = map;
    $[18] = t4;
    $[19] = t5;
  } else {
    t4 = $[18];
    t5 = $[19];
  }
  useEffect(t4, t5);
}
function _temp4(state_2) {
  return state_2.fetchNearByProjects;
}
function _temp3(state_1) {
  return state_1.fetchMapData;
}
function _temp2(state_0) {
  return state_0.mode;
}
function _temp(state) {
  return state.setBounds;
}